import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';

/** The tag as the API reports it (customerPrepaidService.resolveDisplayStatus). */
export type CustomerPrepaidDisplayStatus =
  'active' | 'completed' | 'expired' | 'cancelled' | 'unpaid';

/** One wash line of a book: "Champion × 5". */
export interface CustomerPrepaidWash {
  item_id: string;
  wash_type: string | null;
  name: string | null;
  wash_limit: number;
  redeemed: number;
  expires_at: string | null;
}

/** One prepaid wash book the customer has bought (CWS-1833). */
export interface CustomerPrepaidPurchase {
  prepaid_purchase_id: string;
  /** The sign-up order; Cancel prepaid keys the shared cancel & refund panel on it. */
  order_id: string;
  order_number: string | null;
  book_id: string;
  book_name: string;
  /** CWS-1908: the redemption code, so staff can read it back to a customer who lost the SMS. */
  redemption_code?: string | null;
  price: string | number | null;
  /** `orders.source`: where it was sold (shop, Way app, website, or a vendor import). */
  source: string | null;
  order_source: string | null;
  payment_status: string | null;
  display_status: CustomerPrepaidDisplayStatus;
  is_live: boolean;
  vehicle_id: string | null;
  license_plate: string | null;
  vehicle_state: string | null;
  vehicle_year?: number | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  total_washes: number;
  redeemed_washes: number;
  remaining_washes: number;
  washes: CustomerPrepaidWash[];
  expires_at: string | null;
  last_washed_at: string | null;
  purchased_at: string | null;
  cancelled_at: string | null;
  /** The card the book was bought with, from the payment's snapshot; null for cash or none. */
  card_on_file: { brand: string | null; last4: string } | null;
  /**
   * A live book sold by Way. The card offers Cancel prepaid when this is true and cancel &
   * refund is on at the location; the options endpoint re-checks.
   */
  can_cancel: boolean;
  /**
   * CWS-1881: who bought the book — from the purchase's own customer, which stays the buyer
   * however many relatives redeem from it.
   */
  purchaser: CustomerPrepaidPurchaser;
}

/** The "Purchased by" block of a card (CWS-1881). */
export interface CustomerPrepaidPurchaser {
  name: string | null;
  phone: string | null;
  email: string | null;
  plate: string | null;
}

/**
 * One wash drawn off a prepaid book, with whoever took it (CWS-1881).
 *
 * `customer_id` is set only when a customer record exists for the redeemer — a phone was
 * shared. That is what decides whether the row gets a "view customer" link; a name-and-
 * email-only redemption has none, and an anonymous walk-in is a plate and nothing else.
 */
export interface CustomerPrepaidRedemption {
  id: string;
  redeemed_at: string;
  plate: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  wash_name: string | null;
  customer_id: string | null;
}

/**
 * Prepaid wash books for the Customers → Prepaid tab (CWS-1833).
 *
 * Read-only. Cancelling a book goes through CancelRefundService keyed on the
 * purchase's `order_id`.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPrepaidService {
  constructor(private readonly apiService: ApiService) {}

  getCustomerPrepaidPurchases(customerId: string): Observable<CustomerPrepaidPurchase[]> {
    return this.apiService
      .get<CustomerPrepaidPurchase[]>(
        `order/prepaid-purchases/customer/${encodeURIComponent(customerId)}`
      )
      .pipe(map(response => response.data ?? []));
  }

  /** CWS-1881: the redemption log for one book, newest first. Loaded when its section opens. */
  getPrepaidRedemptions(purchaseId: string): Observable<CustomerPrepaidRedemption[]> {
    return this.apiService
      .get<CustomerPrepaidRedemption[]>(
        `order/prepaid-purchases/${encodeURIComponent(purchaseId)}/redemptions`
      )
      .pipe(map(response => response.data ?? []));
  }
}
